import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';

interface RouteResult {
  success: boolean;
  quote_id: string;
  policy_version: number;
  expires_at: string;
  items: Array<{
    item_id: string;
    eligible: boolean;
    product: {
      identified: boolean;
      title: string;
      brand: string;
      category: string;
    };
    assessment: {
      condition_grade: string;
      confidence: number;
      estimated_value: number;
    };
    decision: {
      channel: string;
      confidence: number;
      reasoning: string;
    };
    offer: {
      type: string;
      amount: number;
      currency: string;
    };
    ineligible_reason?: string;
  }>;
  summary: {
    total_items: number;
    eligible_items: number;
    total_offer: number;
    currency: string;
  };
  next_steps: {
    action: string;
    options: Array<{
      type: string;
      instructions: string;
    }>;
  };
  processing_time_ms: number;
}

export const Route = createFileRoute('/route-tester')({
  component: RouteTester,
});

function RouteTester() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<RouteResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Form state
  const [title, setTitle] = useState('Patagonia Better Sweater Fleece');
  const [brand, setBrand] = useState('Patagonia');
  const [category, setCategory] = useState('outerwear');
  const [condition, setCondition] = useState('B+');
  const [imageUrl, setImageUrl] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [loyaltyTier, setLoyaltyTier] = useState('none');

  const testRoute = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    
    try {
      const response = await fetch('/api/v1/route/process', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: 'test-tenant',
          channel: 'web',
          items: [{
            item_id: `item_${Date.now()}`,
            title,
            brand,
            category_hint: category,
            claimed_condition: condition,
            image_url: imageUrl || undefined,
          }],
          customer: {
            id: customerId || `cust_${Date.now()}`,
            loyalty_tier: loyaltyTier === 'none' ? undefined : loyaltyTier,
          },
        }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setResult(data);
      } else {
        setError(data.error || 'Route processing failed');
      }
    } catch (err) {
      setError('Failed to connect to Route API');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-light" style={{ color: '#171717' }}>
            Route Tester
          </h1>
          <p className="text-sm mt-1" style={{ color: '#A9A9A9' }}>
            Test how products are routed and what offers are generated
          </p>
        </div>
        <Link
          to="/programs"
          className="px-4 py-2 rounded-full text-sm font-medium transition-colors border"
          style={{ borderColor: 'rgba(169,169,169,0.3)', color: '#171717' }}
        >
          ← Back to Policies
        </Link>
      </div>

      <div className="grid grid-cols-2 gap-8">
        {/* Input Form */}
        <div className="bg-white border p-6" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
          <h2 className="font-medium mb-6" style={{ color: '#171717' }}>Product Details</h2>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm mb-1" style={{ color: '#666' }}>Product Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Patagonia Fleece Jacket"
                  className="w-full px-3 py-2 border text-sm"
                  style={{ borderColor: 'rgba(169,169,169,0.3)' }}
                />
              </div>
              
              <div>
                <label className="block text-sm mb-1" style={{ color: '#666' }}>Brand</label>
                <input
                  type="text"
                  value={brand}
                  onChange={(e) => setBrand(e.target.value)}
                  placeholder="e.g., Patagonia"
                  className="w-full px-3 py-2 border text-sm"
                  style={{ borderColor: 'rgba(169,169,169,0.3)' }}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm mb-1" style={{ color: '#666' }}>Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-3 py-2 border text-sm bg-white"
                  style={{ borderColor: 'rgba(169,169,169,0.3)' }}
                >
                  <option value="outerwear">Outerwear</option>
                  <option value="tops">Tops</option>
                  <option value="bottoms">Bottoms</option>
                  <option value="footwear">Footwear</option>
                  <option value="accessories">Accessories</option>
                  <option value="bags">Bags</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm mb-1" style={{ color: '#666' }}>Condition Grade</label>
                <select
                  value={condition}
                  onChange={(e) => setCondition(e.target.value)}
                  className="w-full px-3 py-2 border text-sm bg-white"
                  style={{ borderColor: 'rgba(169,169,169,0.3)' }}
                >
                  <option value="A">A - Like New</option>
                  <option value="B+">B+ - Excellent</option>
                  <option value="B">B - Very Good</option>
                  <option value="B-">B- - Good</option>
                  <option value="C+">C+ - Fair</option>
                  <option value="C">C - Acceptable</option>
                  <option value="C-">C- - Poor</option>
                  <option value="D">D - Damaged</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm mb-1" style={{ color: '#666' }}>Image URL (optional)</label>
              <input
                type="url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://example.com/product.jpg"
                className="w-full px-3 py-2 border text-sm"
                style={{ borderColor: 'rgba(169,169,169,0.3)' }}
              />
              <p className="text-xs mt-1" style={{ color: '#A9A9A9' }}>
                Provide an image to enable AI condition assessment
              </p>
            </div>
            
            <div className="pt-4 border-t" style={{ borderColor: 'rgba(169,169,169,0.1)' }}>
              <h3 className="text-sm font-medium mb-3" style={{ color: '#666' }}>Customer Context (optional)</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-1" style={{ color: '#666' }}>Customer ID</label>
                  <input
                    type="text"
                    value={customerId}
                    onChange={(e) => setCustomerId(e.target.value)}
                    placeholder="cust_12345"
                    className="w-full px-3 py-2 border text-sm"
                    style={{ borderColor: 'rgba(169,169,169,0.3)' }}
                  />
                </div>
                
                <div>
                  <label className="block text-sm mb-1" style={{ color: '#666' }}>Loyalty Tier</label>
                  <select
                    value={loyaltyTier}
                    onChange={(e) => setLoyaltyTier(e.target.value)}
                    className="w-full px-3 py-2 border text-sm bg-white"
                    style={{ borderColor: 'rgba(169,169,169,0.3)' }}
                  >
                    <option value="none">None</option>
                    <option value="bronze">Bronze</option>
                    <option value="silver">Silver</option>
                    <option value="gold">Gold</option>
                  </select>
                </div>
              </div>
            </div>
            
            <button
              onClick={testRoute}
              disabled={loading || !title.trim()}
              className="w-full px-4 py-3 rounded-full text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ backgroundColor: '#171717', color: '#FFFFFF' }}
            >
              {loading ? (
                <>
                  <LoadingSpinner />
                  Processing...
                </>
              ) : (
                <>
                  <RouteIcon />
                  Test Route Decision
                </>
              )}
            </button>
          </div>
        </div>

        {/* Results */}
        <div className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 p-4">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
          
          {result && (
            <>
              {/* Summary Card */}
              <div className="bg-white border overflow-hidden" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
                <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: 'rgba(169,169,169,0.1)', backgroundColor: 'rgba(0,251,169,0.1)' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#00FBA9' }}>
                      <CheckIcon />
                    </div>
                    <div>
                      <p className="font-medium" style={{ color: '#171717' }}>Route Decision Complete</p>
                      <p className="text-xs" style={{ color: '#666' }}>{result.processing_time_ms}ms processing time</p>
                    </div>
                  </div>
                  <span className="font-mono text-xs px-2 py-1 rounded" style={{ backgroundColor: '#fff', color: '#666' }}>
                    {result.quote_id}
                  </span>
                </div>
                
                <div className="p-6">
                  {/* Item Result */}
                  {result.items.map((item, idx) => (
                    <div key={idx} className="space-y-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium" style={{ color: '#171717' }}>{item.product.title}</p>
                          <p className="text-sm" style={{ color: '#A9A9A9' }}>{item.product.brand} · {item.product.category}</p>
                        </div>
                        {item.eligible ? (
                          <span className="px-2 py-1 rounded text-xs font-medium" style={{ backgroundColor: 'rgba(0,251,169,0.2)', color: '#00A870' }}>
                            Eligible
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded text-xs font-medium" style={{ backgroundColor: 'rgba(239,68,68,0.1)', color: '#DC2626' }}>
                            Not Eligible
                          </span>
                        )}
                      </div>
                      
                      {/* Routing Decision */}
                      <div className="grid grid-cols-3 gap-4">
                        <div className="p-3" style={{ backgroundColor: '#FAFAFA' }}>
                          <p className="text-xs uppercase tracking-wider mb-1" style={{ color: '#A9A9A9' }}>Condition</p>
                          <p className="text-lg font-mono" style={{ color: '#171717' }}>{item.assessment.condition_grade}</p>
                        </div>
                        <div className="p-3" style={{ backgroundColor: getChannelBg(item.decision.channel) }}>
                          <p className="text-xs uppercase tracking-wider mb-1" style={{ color: '#A9A9A9' }}>Channel</p>
                          <p className="text-lg font-medium capitalize" style={{ color: '#171717' }}>{item.decision.channel}</p>
                        </div>
                        <div className="p-3" style={{ backgroundColor: '#FAFAFA' }}>
                          <p className="text-xs uppercase tracking-wider mb-1" style={{ color: '#A9A9A9' }}>Offer</p>
                          <p className="text-lg font-medium" style={{ color: '#171717' }}>
                            {item.offer.currency === 'GBP' ? '£' : '$'}{item.offer.amount}
                          </p>
                        </div>
                      </div>
                      
                      {/* Reasoning */}
                      <div className="p-3" style={{ backgroundColor: '#FAFAFA' }}>
                        <p className="text-xs uppercase tracking-wider mb-1" style={{ color: '#A9A9A9' }}>Reasoning</p>
                        <p className="text-sm" style={{ color: '#666' }}>{item.decision.reasoning}</p>
                      </div>
                      
                      {/* Offer Details */}
                      <div className="flex items-center gap-2 text-sm" style={{ color: '#666' }}>
                        <span>Offer type:</span>
                        <span className="font-mono px-2 py-0.5 rounded" style={{ backgroundColor: '#FAFAFA' }}>{item.offer.type}</span>
                        <span>·</span>
                        <span>Expires:</span>
                        <span className="font-mono">{new Date(result.expires_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Next Steps */}
              <div className="bg-white border p-6" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
                <h3 className="font-medium mb-4" style={{ color: '#171717' }}>Next Steps</h3>
                <div className="space-y-2">
                  {result.next_steps.options.map((option, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3" style={{ backgroundColor: '#FAFAFA' }}>
                      <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#fff' }}>
                        {option.type === 'store_drop_off' ? <StoreIcon /> : <ShipIcon />}
                      </div>
                      <div>
                        <p className="text-sm font-medium capitalize" style={{ color: '#171717' }}>
                          {option.type.replace(/_/g, ' ')}
                        </p>
                        <p className="text-xs" style={{ color: '#A9A9A9' }}>{option.instructions}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Raw Response */}
              <details className="bg-white border overflow-hidden" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
                <summary className="px-6 py-4 cursor-pointer text-sm font-medium" style={{ color: '#666' }}>
                  View Raw Response
                </summary>
                <pre className="px-6 py-4 text-xs overflow-auto" style={{ backgroundColor: '#FAFAFA', maxHeight: '300px' }}>
                  {JSON.stringify(result, null, 2)}
                </pre>
              </details>
            </>
          )}
          
          {!result && !error && (
            <div className="bg-white border p-12 text-center" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#FAFAFA' }}>
                <RouteIcon size={32} />
              </div>
              <p className="text-sm" style={{ color: '#171717' }}>Enter product details and test</p>
              <p className="text-xs mt-1" style={{ color: '#A9A9A9' }}>
                See how the routing engine evaluates products
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Helper
function getChannelBg(channel: string): string {
  const colors: Record<string, string> = {
    resell: 'rgba(0,251,169,0.2)',
    refurbish: 'rgba(59,130,246,0.15)',
    donate: 'rgba(168,85,247,0.15)',
    recycle: 'rgba(169,169,169,0.15)',
    reject: 'rgba(239,68,68,0.1)',
  };
  return colors[channel] || colors.recycle;
}

// Icons
function RouteIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M9 20L3 17V4L9 7M9 20L15 17M9 20V7M15 17L21 20V7L15 4M15 17V4M9 7L15 4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#171717" strokeWidth="2">
      <path d="M5 13L9 17L19 7" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function StoreIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 9L12 2L21 9V20C21 20.5523 20.5523 21 20 21H4C3.44772 21 3 20.5523 3 20V9Z" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M9 21V12H15V21" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function ShipIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M16 16H21V11.5L17.5 8H16M16 16V8M16 16H8M16 8H3V16H8M8 16V8" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="6" cy="18" r="2" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="18" cy="18" r="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function LoadingSpinner() {
  return (
    <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.2"/>
      <path d="M12 2C6.47715 2 2 6.47715 2 12" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
    </svg>
  );
}

